// Configuration de l'API
const API_CONFIG = {
  BASE_URL: process.env.REACT_APP_API_URL || 'http://localhost:8080/api',
  TIMEOUT: 10000, // 10 secondes
};

// Endpoints - Correspondant exactement aux endpoints du backend Java Spring
export const API_ENDPOINTS = {
  AUTH: {
    LOGIN: '/auth/login',
    REGISTER: '/auth/register',
    LOGOUT: '/auth/logout',
    REFRESH: '/auth/refresh',
    VERIFY: '/auth/verify',
  },
  FESTIVALS: {
    GET_ALL: '/festivals',
    GET_BY_ID: (id) => `/festivals/${id}`,
    CREATE: '/festivals',
    UPDATE: (id) => `/festivals/${id}`,
    DELETE: (id) => `/festivals/${id}`,
    GET_TOURNAMENTS: (id) => `/festivals/${id}/tournaments`,
    IMPORT: '/festivals/import',
  },
  FESTIVAL_EXPENSES: {
    GET_MINE: '/festival-expenses/me',
    CREATE: '/festival-expenses',
    UPDATE: (id) => `/festival-expenses/${id}`,
    DELETE: (id) => `/festival-expenses/${id}`,
    ATTACHMENTS: (id) => `/festival-expenses/${id}/attachments`,
    ATTACHMENT: (attachmentId) => `/festival-expenses/attachments/${attachmentId}`,
    EXPORT: (festivalId) => `/festival-expenses/export/${festivalId}`,
  },
  TOURNAMENTS: {
    GET_ALL: '/tournaments',
    GET_BY_ID: (festivalId, tournamentId) => `/festivals/${festivalId}/tournaments/${tournamentId}`,
    CREATE: (festivalId) => `/festivals/${festivalId}/tournaments`,
    UPDATE: (festivalId, tournamentId) => `/festivals/${festivalId}/tournaments/${tournamentId}`,
    DELETE: (festivalId, tournamentId) => `/festivals/${festivalId}/tournaments/${tournamentId}`,
  },
  TOURNAMENT_SERIES: {
    GET_ALL: (festivalId) => `/festivals/${festivalId}/series`,
    CREATE: (festivalId) => `/festivals/${festivalId}/series`,
    UPDATE: (festivalId, seriesId) => `/festivals/${festivalId}/series/${seriesId}`,
    DELETE: (festivalId, seriesId) => `/festivals/${festivalId}/series/${seriesId}`,
    SET_TOURNAMENTS: (festivalId, seriesId) => `/festivals/${festivalId}/series/${seriesId}/tournaments`,
    SET_MY_RESULT: (festivalId, seriesId) => `/festivals/${festivalId}/series/${seriesId}/my-result`,
  },
  VENUES: {
    GET_ALL: '/venues',
    GET_BY_ID: (id) => `/venues/${id}`,
    CREATE: '/venues',
    UPDATE: (id) => `/venues/${id}`,
    DELETE: (id) => `/venues/${id}`,
  },
  CIRCUITS: {
    GET_ALL: '/circuits',
    GET_BY_ID: (id) => `/circuits/${id}`,
    CREATE: '/circuits',
    UPDATE: (id) => `/circuits/${id}`,
    DELETE: (id) => `/circuits/${id}`,
  },
  USER: {
    PROFILE: '/user/profile',
    UPDATE_PROFILE: '/user/profile',
  },
  USERS: {
    GET_ALL: '/users',
    UPDATE_ROLE: (id) => `/users/${id}/role`,
    UPDATE_ENABLED: (id) => `/users/${id}/enabled`,
    DELETE: (id) => `/users/${id}`,
    RESET_PASSWORD: (id) => `/users/${id}/reset-password`,
  },
  ACCOUNT: {
    CHANGE_PASSWORD: '/account/password',
    ME: '/account/me',
  },
  PARTICIPATIONS: {
    GET_MINE: '/participations/me',
    GET_BY_TOURNAMENT: (tournamentId) => `/participations/tournament/${tournamentId}`,
    CONFIRM: (id) => `/participations/${id}/confirm`,
    CANCEL: (id) => `/participations/${id}/cancel`,
    DELETE: (id) => `/participations/${id}`,
    SET_RESULT: (id) => `/participations/${id}/result`,
    SET_MY_RESULT: (id) => `/participations/${id}/my-result`,
    LEADERBOARD: '/participations/leaderboard',
    ATTACHMENTS: (id) => `/participations/${id}/attachments`,
    DOWNLOAD_ATTACHMENT: (attachmentId) => `/participations/attachments/${attachmentId}`,
    DELETE_ATTACHMENT: (attachmentId) => `/participations/attachments/${attachmentId}`,
  }
};

// Fonction helper pour construire l'URL complète
export const buildUrl = (endpoint) => {
  return `${API_CONFIG.BASE_URL}${endpoint}`;
};

// Classe d'erreur personnalisée pour les erreurs API
class ApiError extends Error {
  constructor(status, data) {
    super(data?.message || 'Une erreur est survenue');
    this.name = 'ApiError';
    this.response = {
      status: status,
      data: data
    };
  }
}

// Fonction pour gérer les erreurs API
export const handleApiError = (error) => {
  if (error.response) {
    const status = error.response.status;
    const message = error.response.data?.message || 'Une erreur est survenue';

    switch (status) {
      case 400:
        return { error: 'Données invalides', details: message };
      case 401:
        return { error: 'Non authentifié', details: 'Veuillez vous reconnecter' };
      case 403:
        return { error: 'Accès refusé', details: message };
      case 404:
        return { error: 'Ressource non trouvée', details: message };
      case 500:
        return { error: 'Erreur serveur', details: 'Veuillez réessayer plus tard' };
      default:
        return { error: 'Erreur', details: message };
    }
  } else if (error.request) {
    return { error: 'Pas de réponse du serveur', details: 'Vérifiez votre connexion' };
  } else {
    return { error: 'Erreur', details: error.message };
  }
};

// Helper pour les requêtes avec authentification
const fetchWithAuth = async (url, options = {}) => {
  const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');

  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  try {
    const response = await fetch(url, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Erreur serveur' }));
      throw new ApiError(response.status, error);
    }

    const data = await response.json();

    // Le backend Java renvoie un ApiResponse avec { success, message, data, total }
    // On extrait les données pour garder la compatibilité avec le frontend
    return {
      data: data.data || data,
      success: data.success !== undefined ? data.success : true,
      message: data.message,
      total: data.total
    };
  } catch (error) {
    throw error;
  }
};

// Fonction de conversion snake_case -> camelCase pour les données reçues
const convertToCamelCase = (obj) => {
  if (obj === null || obj === undefined) return obj;

  if (Array.isArray(obj)) {
    return obj.map(item => convertToCamelCase(item));
  }

  if (typeof obj === 'object' && obj.constructor === Object) {
    return Object.keys(obj).reduce((acc, key) => {
      const camelKey = key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
      acc[camelKey] = convertToCamelCase(obj[key]);
      return acc;
    }, {});
  }

  return obj;
};

// Fonction de conversion camelCase -> snake_case pour les données envoyées
const convertToSnakeCase = (obj) => {
  if (obj === null || obj === undefined) return obj;

  if (Array.isArray(obj)) {
    return obj.map(item => convertToSnakeCase(item));
  }

  if (typeof obj === 'object' && obj.constructor === Object) {
    return Object.keys(obj).reduce((acc, key) => {
      const snakeKey = key.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
      acc[snakeKey] = convertToSnakeCase(obj[key]);
      return acc;
    }, {});
  }

  return obj;
};

// =============================================
// FESTIVALS API
// =============================================

export const festivalsAPI = {
  // Récupère tous les festivals
  getAll: async (params = {}) => {
    const queryParams = new URLSearchParams(params).toString();
    const url = buildUrl(API_ENDPOINTS.FESTIVALS.GET_ALL + (queryParams ? `?${queryParams}` : ''));
    const response = await fetchWithAuth(url, { method: 'GET' });

    // Convertir les données en camelCase
    const festivals = convertToCamelCase(response.data);

    // ✅ Le backend renvoie déjà subEvents, pas besoin d'adaptation
    return { ...response, data: festivals };
  },

  // Récupère un festival par ID
  getById: async (id) => {
    const url = buildUrl(API_ENDPOINTS.FESTIVALS.GET_BY_ID(id));
    const response = await fetchWithAuth(url, { method: 'GET' });

    // ✅ Le backend renvoie déjà subEvents
    const festival = convertToCamelCase(response.data);
    return { ...response, data: festival };
  },

  // Crée un festival
  create: async (festivalData) => {
    const url = buildUrl(API_ENDPOINTS.FESTIVALS.CREATE);

    // Adapter le format avant l'envoi (subEvents -> tournaments)
    const adaptedData = {
      ...festivalData,
      tournaments: festivalData.subEvents || festivalData.tournaments || [],
      subEvents: undefined
    };

    const response = await fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(convertToSnakeCase(adaptedData)),
    });

    // ✅ Le backend renvoie déjà subEvents
    const festival = convertToCamelCase(response.data);
    return { ...response, data: festival };
  },

  // Met à jour un festival
  update: async (id, festivalData) => {
    const url = buildUrl(API_ENDPOINTS.FESTIVALS.UPDATE(id));

    // Adapter le format avant l'envoi
    const adaptedData = {
      ...festivalData,
      tournaments: festivalData.subEvents || festivalData.tournaments || [],
      subEvents: undefined
    };

    const response = await fetchWithAuth(url, {
      method: 'PUT',
      body: JSON.stringify(convertToSnakeCase(adaptedData)),
    });

    // ✅ Le backend renvoie déjà subEvents
    const festival = convertToCamelCase(response.data);
    return { ...response, data: festival };
  },

  // Supprime un festival
  delete: async (id) => {
    const url = buildUrl(API_ENDPOINTS.FESTIVALS.DELETE(id));
    return await fetchWithAuth(url, { method: 'DELETE' });
  },

  // Récupère les tournois d'un festival
  getTournaments: async (festivalId) => {
    const url = buildUrl(API_ENDPOINTS.FESTIVALS.GET_TOURNAMENTS(festivalId));
    const response = await fetchWithAuth(url, { method: 'GET' });
    return { ...response, data: convertToCamelCase(response.data) };
  },

  // Importe un ou plusieurs festivals (avec tournois) depuis un JSON (ADMIN)
  importFestivals: async (payload) => {
    const url = buildUrl(API_ENDPOINTS.FESTIVALS.IMPORT);
    return await fetchWithAuth(url, { method: 'POST', body: JSON.stringify(payload) });
  },
};

// =============================================
// TOURNAMENTS API
// =============================================

export const tournamentsAPI = {
  // Récupère tous les tournois
  getAll: async (params = {}) => {
    const queryParams = new URLSearchParams(params).toString();
    const url = buildUrl(API_ENDPOINTS.TOURNAMENTS.GET_ALL + (queryParams ? `?${queryParams}` : ''));
    const response = await fetchWithAuth(url, { method: 'GET' });
    return { ...response, data: convertToCamelCase(response.data) };
  },

  // Récupère un tournoi par ID
  getById: async (festivalId, tournamentId) => {
    const url = buildUrl(API_ENDPOINTS.TOURNAMENTS.GET_BY_ID(festivalId, tournamentId));
    const response = await fetchWithAuth(url, { method: 'GET' });
    return { ...response, data: convertToCamelCase(response.data) };
  },

  // Crée un tournoi
  // ⚠️ Pas de convertToSnakeCase ici : TournamentDTO/TournamentDetailedInfoDTO
  // attendent explicitement du camelCase côté backend (@JsonProperty("buyIn"),
  // "prizePool", "startingStack", "blindStructure", "venueId"...). Snake-caser
  // ce payload ferait perdre silencieusement ces champs (buy_in ne mappe à
  // rien côté Jackson).
  create: async (festivalId, tournamentData) => {
    const url = buildUrl(API_ENDPOINTS.TOURNAMENTS.CREATE(festivalId));
    const response = await fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(tournamentData),
    });
    return { ...response, data: convertToCamelCase(response.data) };
  },

  // Met à jour un tournoi (voir note ci-dessus sur le format camelCase)
  update: async (festivalId, tournamentId, tournamentData) => {
    const url = buildUrl(API_ENDPOINTS.TOURNAMENTS.UPDATE(festivalId, tournamentId));
    const response = await fetchWithAuth(url, {
      method: 'PUT',
      body: JSON.stringify(tournamentData),
    });
    return { ...response, data: convertToCamelCase(response.data) };
  },

  // Supprime un tournoi
  delete: async (festivalId, tournamentId) => {
    const url = buildUrl(API_ENDPOINTS.TOURNAMENTS.DELETE(festivalId, tournamentId));
    return await fetchWithAuth(url, { method: 'DELETE' });
  },
};

// =============================================
// TOURNAMENT SERIES API (structures multi-jours : Day 1A / Day 1B / Final Day)
// =============================================

export const tournamentSeriesAPI = {
  // Liste les séries d'un festival
  getAll: async (festivalId) => {
    const url = buildUrl(API_ENDPOINTS.TOURNAMENT_SERIES.GET_ALL(festivalId));
    const response = await fetchWithAuth(url, { method: 'GET' });
    return { ...response, data: convertToCamelCase(response.data) };
  },

  // Crée une nouvelle série (totalEntries optionnel : nombre total
  // d'entrées de la série entière, distinct de entriesCount qui est propre
  // à chaque participant)
  create: async (festivalId, name, totalEntries) => {
    const url = buildUrl(API_ENDPOINTS.TOURNAMENT_SERIES.CREATE(festivalId));
    const response = await fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify({ name, totalEntries: totalEntries === '' ? null : totalEntries }),
    });
    return { ...response, data: convertToCamelCase(response.data) };
  },

  // Met à jour le nom et/ou le nombre total d'entrées d'une série existante
  update: async (festivalId, seriesId, { name, totalEntries }) => {
    const url = buildUrl(API_ENDPOINTS.TOURNAMENT_SERIES.UPDATE(festivalId, seriesId));
    const response = await fetchWithAuth(url, {
      method: 'PUT',
      body: JSON.stringify({ name, totalEntries: totalEntries === '' ? null : totalEntries }),
    });
    return { ...response, data: convertToCamelCase(response.data) };
  },

  // Définit en une fois la liste complète des tournois rattachés à la série
  // (les autres en sont détachés)
  setTournaments: async (festivalId, seriesId, tournamentIds) => {
    const url = buildUrl(API_ENDPOINTS.TOURNAMENT_SERIES.SET_TOURNAMENTS(festivalId, seriesId));
    return await fetchWithAuth(url, {
      method: 'PUT',
      body: JSON.stringify({ tournamentIds }),
    });
  },

  // Supprime une série (les tournois rattachés deviennent indépendants)
  delete: async (festivalId, seriesId) => {
    const url = buildUrl(API_ENDPOINTS.TOURNAMENT_SERIES.DELETE(festivalId, seriesId));
    return await fetchWithAuth(url, { method: 'DELETE' });
  },

  // Enregistre MON résultat final (position + cashout) pour l'ensemble
  // d'une série - pas pour un day en particulier (voir MyParticipationsPage)
  setMyResult: async (festivalId, seriesId, { finishPosition, prizeWon, totalEntries }) => {
    const url = buildUrl(API_ENDPOINTS.TOURNAMENT_SERIES.SET_MY_RESULT(festivalId, seriesId));
    const response = await fetchWithAuth(url, {
      method: 'PUT',
      body: JSON.stringify({ finishPosition, prizeWon, totalEntries }),
    });
    return { ...response, data: convertToCamelCase(response.data) };
  },
};

// =============================================
// VENUES API (lieux / casinos)
// =============================================

export const venuesAPI = {
  // Récupère tous les lieux
  getAll: async (params = {}) => {
    const queryParams = new URLSearchParams(params).toString();
    const url = buildUrl(API_ENDPOINTS.VENUES.GET_ALL + (queryParams ? `?${queryParams}` : ''));
    const response = await fetchWithAuth(url, { method: 'GET' });
    return { ...response, data: convertToCamelCase(response.data) };
  },

  // Récupère un lieu par ID
  getById: async (id) => {
    const url = buildUrl(API_ENDPOINTS.VENUES.GET_BY_ID(id));
    const response = await fetchWithAuth(url, { method: 'GET' });
    return { ...response, data: convertToCamelCase(response.data) };
  },

  // Crée un lieu
  create: async (venueData) => {
    const url = buildUrl(API_ENDPOINTS.VENUES.CREATE);
    const response = await fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(convertToSnakeCase(venueData)),
    });
    return { ...response, data: convertToCamelCase(response.data) };
  },

  // Met à jour un lieu
  update: async (id, venueData) => {
    const url = buildUrl(API_ENDPOINTS.VENUES.UPDATE(id));
    const response = await fetchWithAuth(url, {
      method: 'PUT',
      body: JSON.stringify(convertToSnakeCase(venueData)),
    });
    return { ...response, data: convertToCamelCase(response.data) };
  },

  // Supprime un lieu
  delete: async (id) => {
    const url = buildUrl(API_ENDPOINTS.VENUES.DELETE(id));
    return await fetchWithAuth(url, { method: 'DELETE' });
  },
};

// =============================================
// CIRCUITS API (organisateurs de circuits de tournois)
// =============================================

export const circuitsAPI = {
  // Récupère tous les circuits
  getAll: async () => {
    const url = buildUrl(API_ENDPOINTS.CIRCUITS.GET_ALL);
    const response = await fetchWithAuth(url, { method: 'GET' });
    return { ...response, data: convertToCamelCase(response.data) };
  },

  // Récupère un circuit par ID
  getById: async (id) => {
    const url = buildUrl(API_ENDPOINTS.CIRCUITS.GET_BY_ID(id));
    const response = await fetchWithAuth(url, { method: 'GET' });
    return { ...response, data: convertToCamelCase(response.data) };
  },

  // Crée un circuit
  create: async (circuitData) => {
    const url = buildUrl(API_ENDPOINTS.CIRCUITS.CREATE);
    const response = await fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify(convertToSnakeCase(circuitData)),
    });
    return { ...response, data: convertToCamelCase(response.data) };
  },

  // Met à jour un circuit
  update: async (id, circuitData) => {
    const url = buildUrl(API_ENDPOINTS.CIRCUITS.UPDATE(id));
    const response = await fetchWithAuth(url, {
      method: 'PUT',
      body: JSON.stringify(convertToSnakeCase(circuitData)),
    });
    return { ...response, data: convertToCamelCase(response.data) };
  },

  // Supprime un circuit
  delete: async (id) => {
    const url = buildUrl(API_ENDPOINTS.CIRCUITS.DELETE(id));
    return await fetchWithAuth(url, { method: 'DELETE' });
  },
};

// =============================================
// PARTICIPATIONS API (inscriptions et résultats, admin pour l'essentiel)
// =============================================
// ⚠️ Pas de convertToSnakeCase sur les payloads envoyés : ParticipationDTO
// n'a aucune annotation @JsonProperty (donc pas de renommage), les champs
// sont exposés tels quels en camelCase côté backend (mêmes raisons que
// pour tournamentsAPI, voir la note plus haut).

export const participationsAPI = {
  // Récupère toutes mes participations
  getMine: async () => {
    const url = buildUrl(API_ENDPOINTS.PARTICIPATIONS.GET_MINE);
    const response = await fetchWithAuth(url, { method: 'GET' });
    return { ...response, data: convertToCamelCase(response.data) };
  },

  // Récupère les participants d'un tournoi (admin)
  getByTournament: async (tournamentId) => {
    const url = buildUrl(API_ENDPOINTS.PARTICIPATIONS.GET_BY_TOURNAMENT(tournamentId));
    const response = await fetchWithAuth(url, { method: 'GET' });
    return { ...response, data: convertToCamelCase(response.data) };
  },

  // Confirme une participation (admin)
  confirm: async (id) => {
    const url = buildUrl(API_ENDPOINTS.PARTICIPATIONS.CONFIRM(id));
    const response = await fetchWithAuth(url, { method: 'PUT' });
    return { ...response, data: convertToCamelCase(response.data) };
  },

  // Annule une participation (admin)
  cancel: async (id) => {
    const url = buildUrl(API_ENDPOINTS.PARTICIPATIONS.CANCEL(id));
    const response = await fetchWithAuth(url, { method: 'PUT' });
    return { ...response, data: convertToCamelCase(response.data) };
  },

  // Supprime une participation (admin)
  delete: async (id) => {
    const url = buildUrl(API_ENDPOINTS.PARTICIPATIONS.DELETE(id));
    return await fetchWithAuth(url, { method: 'DELETE' });
  },

  // Enregistre le résultat d'un participant (position + gain + entrées + buy-in) (admin)
  setResult: async (id, { finishPosition, prizeWon, entriesCount, buyInPaid }) => {
    const url = buildUrl(API_ENDPOINTS.PARTICIPATIONS.SET_RESULT(id));
    const response = await fetchWithAuth(url, {
      method: 'PUT',
      body: JSON.stringify({ finishPosition, prizeWon, entriesCount, buyInPaid }),
    });
    return { ...response, data: convertToCamelCase(response.data) };
  },

  // Enregistre MON PROPRE résultat, après avoir joué un tournoi
  setMyResult: async (id, { finishPosition, prizeWon, entriesCount, buyInPaid, totalEntries }) => {
    const url = buildUrl(API_ENDPOINTS.PARTICIPATIONS.SET_MY_RESULT(id));
    const response = await fetchWithAuth(url, {
      method: 'PUT',
      body: JSON.stringify({ finishPosition, prizeWon, entriesCount, buyInPaid, totalEntries }),
    });
    return { ...response, data: convertToCamelCase(response.data) };
  },

  // Classement général (public)
  getLeaderboard: async () => {
    const url = buildUrl(API_ENDPOINTS.PARTICIPATIONS.LEADERBOARD);
    const response = await fetchWithAuth(url, { method: 'GET' });
    return { ...response, data: convertToCamelCase(response.data) };
  },

  // Liste les pièces jointes d'une participation
  getAttachments: async (id) => {
    const url = buildUrl(API_ENDPOINTS.PARTICIPATIONS.ATTACHMENTS(id));
    const response = await fetchWithAuth(url, { method: 'GET' });
    return { ...response, data: convertToCamelCase(response.data) };
  },

  // Envoie une pièce jointe (photo/PDF). Pas de fetchWithAuth ici : il force
  // Content-Type: application/json, ce qui casse un envoi multipart (le
  // navigateur doit fixer lui-même le boundary du Content-Type).
  uploadAttachment: async (id, file, amount) => {
    const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
    const formData = new FormData();
    formData.append('file', file);
    if (amount !== undefined && amount !== null && amount !== '') {
      formData.append('amount', amount);
    }

    const response = await fetch(buildUrl(API_ENDPOINTS.PARTICIPATIONS.ATTACHMENTS(id)), {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({
        message: response.status === 413 ? 'Le fichier est trop volumineux (10 Mo maximum)' : 'Erreur serveur',
      }));
      throw new ApiError(response.status, error);
    }

    const data = await response.json();
    return { ...data, data: convertToCamelCase(data.data) };
  },

  // URL de téléchargement/aperçu d'une pièce jointe (à utiliser avec le
  // token, voir downloadAttachment ci-dessous pour un usage direct <a>/<img>)
  getAttachmentUrl: (attachmentId) => buildUrl(API_ENDPOINTS.PARTICIPATIONS.DOWNLOAD_ATTACHMENT(attachmentId)),

  // Télécharge le contenu d'une pièce jointe (protégée par token) et
  // renvoie une URL blob locale, utilisable dans <a href> ou <img src>.
  downloadAttachment: async (attachmentId) => {
    const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
    const response = await fetch(buildUrl(API_ENDPOINTS.PARTICIPATIONS.DOWNLOAD_ATTACHMENT(attachmentId)), {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok) {
      throw new ApiError(response.status, { message: 'Impossible de récupérer la pièce jointe' });
    }
    const blob = await response.blob();
    return URL.createObjectURL(blob);
  },

  // Supprime une pièce jointe
  deleteAttachment: async (attachmentId) => {
    const url = buildUrl(API_ENDPOINTS.PARTICIPATIONS.DELETE_ATTACHMENT(attachmentId));
    return await fetchWithAuth(url, { method: 'DELETE' });
  },
};

// =============================================
// USERS API (gestion des comptes, admin uniquement)
// =============================================

// =============================================
// FESTIVAL EXPENSES API (frais d'un festival, hors buy-in)
// =============================================
export const festivalExpensesAPI = {
  getMine: async () => {
    const url = buildUrl(API_ENDPOINTS.FESTIVAL_EXPENSES.GET_MINE);
    const response = await fetchWithAuth(url, { method: 'GET' });
    return { ...response, data: convertToCamelCase(response.data) };
  },

  create: async ({ festivalId, category, label, amount, expenseDate }) => {
    const url = buildUrl(API_ENDPOINTS.FESTIVAL_EXPENSES.CREATE);
    const response = await fetchWithAuth(url, {
      method: 'POST',
      body: JSON.stringify({ festivalId, category, label, amount, expenseDate }),
    });
    return { ...response, data: convertToCamelCase(response.data) };
  },

  delete: async (id) => {
    const url = buildUrl(API_ENDPOINTS.FESTIVAL_EXPENSES.DELETE(id));
    return await fetchWithAuth(url, { method: 'DELETE' });
  },

  // Envoie un justificatif (photo/PDF). Pas de fetchWithAuth : il force
  // Content-Type: application/json, ce qui casserait l'envoi multipart.
  uploadAttachment: async (expenseId, file) => {
    const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(buildUrl(API_ENDPOINTS.FESTIVAL_EXPENSES.ATTACHMENTS(expenseId)), {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({
        message: response.status === 413 ? 'Le fichier est trop volumineux (10 Mo maximum)' : 'Erreur serveur',
      }));
      throw new ApiError(response.status, error);
    }

    const data = await response.json();
    return { ...data, data: convertToCamelCase(data.data) };
  },

  // Télécharge un justificatif (protégé par token) et renvoie une URL blob locale.
  downloadAttachment: async (attachmentId) => {
    const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
    const response = await fetch(buildUrl(API_ENDPOINTS.FESTIVAL_EXPENSES.ATTACHMENT(attachmentId)), {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok) {
      throw new ApiError(response.status, { message: 'Impossible de récupérer le justificatif' });
    }
    const blob = await response.blob();
    return URL.createObjectURL(blob);
  },

  deleteAttachment: async (attachmentId) => {
    const url = buildUrl(API_ENDPOINTS.FESTIVAL_EXPENSES.ATTACHMENT(attachmentId));
    return await fetchWithAuth(url, { method: 'DELETE' });
  },

  // Archive ZIP de tous mes justificatifs d'un festival (frais de festival +
  // tournois, avec récapitulatif CSV). Renvoie { blob, fileName }.
  exportReceipts: async (festivalId) => {
    const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
    const response = await fetch(buildUrl(API_ENDPOINTS.FESTIVAL_EXPENSES.EXPORT(festivalId)), {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Erreur serveur' }));
      throw new ApiError(response.status, error);
    }
    // Nom proposé par le serveur (Content-Disposition: filename*=UTF-8''...), sinon nom par défaut.
    const disposition = response.headers.get('Content-Disposition') || '';
    const match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
    let fileName = 'justificatifs.zip';
    if (match) {
      try {
        fileName = decodeURIComponent(match[1]);
      } catch (e) {
        // garde le nom par défaut
      }
    }
    return { blob: await response.blob(), fileName };
  },
};

export const usersAPI = {
  // Récupère tous les utilisateurs
  getAll: async () => {
    const url = buildUrl(API_ENDPOINTS.USERS.GET_ALL);
    const response = await fetchWithAuth(url, { method: 'GET' });
    return { ...response, data: convertToCamelCase(response.data) };
  },

  // Change le rôle d'un utilisateur (USER / ADMIN)
  updateRole: async (id, role) => {
    const url = buildUrl(API_ENDPOINTS.USERS.UPDATE_ROLE(id));
    const response = await fetchWithAuth(url, {
      method: 'PUT',
      body: JSON.stringify({ role }),
    });
    return { ...response, data: convertToCamelCase(response.data) };
  },

  // Active ou désactive un compte
  setEnabled: async (id, enabled) => {
    const url = buildUrl(API_ENDPOINTS.USERS.UPDATE_ENABLED(id));
    const response = await fetchWithAuth(url, {
      method: 'PUT',
      body: JSON.stringify({ enabled }),
    });
    return { ...response, data: convertToCamelCase(response.data) };
  },

  // Supprime un utilisateur
  delete: async (id) => {
    const url = buildUrl(API_ENDPOINTS.USERS.DELETE(id));
    return await fetchWithAuth(url, { method: 'DELETE' });
  },

  // Réinitialise le mot de passe d'un utilisateur (génère un mot de passe
  // temporaire, renvoyé une seule fois, à communiquer manuellement)
  resetPassword: async (id) => {
    const url = buildUrl(API_ENDPOINTS.USERS.RESET_PASSWORD(id));
    const response = await fetchWithAuth(url, { method: 'POST' });
    return { ...response, data: convertToCamelCase(response.data) };
  },
};

// =============================================
// ACCOUNT API (actions sur son propre compte)
// =============================================

export const accountAPI = {
  // Change son propre mot de passe (utilisé aussi après une réinitialisation
  // admin, avec le mot de passe temporaire comme mot de passe actuel)
  changePassword: async (currentPassword, newPassword) => {
    const url = buildUrl(API_ENDPOINTS.ACCOUNT.CHANGE_PASSWORD);
    return await fetchWithAuth(url, {
      method: 'PUT',
      body: JSON.stringify({ currentPassword, newPassword }),
    });
  },

  // Profil du compte connecté
  getProfile: async () => {
    const response = await fetchWithAuth(buildUrl(API_ENDPOINTS.ACCOUNT.ME));
    return { ...response, data: convertToCamelCase(response.data) };
  },

  // Modifie nom d'utilisateur / email. Renvoie un nouveau token
  // (l'email est le sujet du JWT) : à passer à updateSession().
  updateProfile: async ({ username, email, currentPassword }) => {
    const response = await fetchWithAuth(buildUrl(API_ENDPOINTS.ACCOUNT.ME), {
      method: 'PUT',
      body: JSON.stringify({ username, email, currentPassword }),
    });
    return { ...response, data: convertToCamelCase(response.data) };
  },
};

// =============================================
// AUTH API
// =============================================

export const authAPI = {
  // Connexion
  login: async (credentials) => {
    const url = buildUrl(API_ENDPOINTS.AUTH.LOGIN);
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(credentials),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Erreur serveur' }));
      throw new ApiError(response.status, error);
    }

    const data = await response.json();
    return {
      data: convertToCamelCase(data.data || data),
      success: data.success !== undefined ? data.success : true,
      message: data.message
    };
  },

  // Inscription
  register: async (userData) => {
    const url = buildUrl(API_ENDPOINTS.AUTH.REGISTER);
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(userData),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Erreur serveur' }));
      throw new ApiError(response.status, error);
    }

    const data = await response.json();
    return {
      data: convertToCamelCase(data.data || data),
      success: data.success !== undefined ? data.success : true,
      message: data.message
    };
  },
};

// Export de la configuration
export default API_CONFIG;